<template>
  <HeaderComponent />
  <main class="pt-[70px] w-full h-screen bg-gray-100 relative flex">
    <SideBarComponent />
    <div class="lg:w-[calc(100%-250px)] md:w-[calc(100%-78px)] lg:left-[250px] md:left-[78px] relative top-0 overflow-y-auto h-full md:p-[25px] p-[20px]">
      <section class="w-full relative flex items-center justify-center">
        <div class="w-full">
          <!-- En-tête -->
          <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">Tableau de bord des réservations</h1>
            <p class="text-gray-600 mt-2">Aperçu global des performances et statistiques</p>
          </div>
      
          <!-- Cartes de statistiques -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <!-- Carte 1: Réservations totales -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-500">Réservations</p>
                  <p class="text-3xl font-bold mt-2">{{ stats.totalReservations }}</p>
                </div>
                <div class="p-3 rounded-full bg-[#D4AF37]/10">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          
            <!-- Carte 2: Chiffre d'affaires (REEL) -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-500">Chiffre d'affaires</p>
                  <p class="text-3xl font-bold mt-2">{{ formatPrice(stats.totalPaid) }} FCFA</p>
                  <p class="text-xs text-gray-500 mt-1">Montant réellement encaissé</p>
                </div>
                <div class="p-3 rounded-full bg-green-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          
            <!-- Carte 3: Potentiel (THEORIQUE) -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-500">Potentiel total</p>
                  <p class="text-3xl font-bold mt-2">{{ formatPrice(stats.totalAmount) }} FCFA</p>
                  <p class="text-xs text-gray-500 mt-1">Valeur théorique</p>
                </div>
                <div class="p-3 rounded-full bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1f2937]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          
            <!-- Carte 4: À encaisser -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-500">À encaisser</p>
                  <p class="text-3xl font-bold mt-2">{{ formatPrice(stats.totalReceivable) }} FCFA</p>
                  <p class="text-xs text-gray-500 mt-1">{{ Math.round((stats.totalReceivable / stats.totalAmount) * 100) || 0 }}% du potentiel</p>
                </div>
                <div class="p-3 rounded-full bg-orange-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
      
          <!-- Graphiques et visualisations -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <!-- Graphique des réservations par mois -->
            <div class="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
              <h2 class="text-lg font-medium text-gray-900 mb-4">Réservations par mois</h2>
              <div class="h-80">
                <ReservationsChart :monthly-stats="monthlyStats" />
              </div>
            </div>
          
            <!-- Répartition par statut -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <h2 class="text-lg font-medium text-gray-900 mb-4">Statut des réservations</h2>
              <div class="h-80">
                <DoughnutChart :stats="monthlyStats" />
              </div>
            </div>
          </div>
      
          <!-- Méthodes de paiement et autres stats -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Méthodes de paiement -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <h2 class="text-lg font-medium text-gray-900 mb-4">Méthodes de paiement</h2>
              <div class="space-y-4">
                <div v-for="(data, method) in stats.byPaymentMethod" :key="method" class="flex items-center">
                  <div class="w-2/5">
                    <span class="capitalize">{{ getPaymentMethodText(method) }}</span>
                  </div>
                  <div class="w-3/5">
                    <div class="flex items-center">
                      <div class="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          :class="getPaymentMethodColor(method)"
                          class="h-2.5 rounded-full" 
                          :style="{ width: `${(data.count / stats.totalReservations) * 100}%` }"
                        ></div>
                      </div>
                      <span class="ml-2 text-sm flex text-gray-500">
                        {{ data.count }} ({{ Math.round((data.count / stats.totalReservations) * 100) }}%)
                      </span>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ formatPrice(data.amount) }} FCFA
                    </p>
                  </div>
                </div>
              </div>
            </div>
          
            <!-- Dernières réservations -->
            <div class="bg-white rounded-xl shadow-md p-6">
              <h2 class="text-lg font-medium text-gray-900 mb-4">Dernières réservations</h2>
              <div class="space-y-4">
                <div v-for="reservation in recentReservations" :key="reservation._id" class="border-b pb-3 last:border-0">
                  <div class="flex justify-between items-start">
                    <div>
                      <p class="font-medium">{{ reservation.fullName }}</p>
                      <p class="text-sm text-gray-500">{{ formatDate(reservation.createdAt) }}</p>
                    </div>
                    <span :class="getStatusClasses(reservation.status)" class="px-2 py-1 rounded-full text-xs">
                      {{ getStatusText(reservation.status) }}
                    </span>
                  </div>
                  <div class="flex justify-between mt-1">
                    <p class="text-sm">{{ formatPrice(reservation.totalPrice) }} FCFA</p>
                    <p class="text-sm text-gray-500">
                      {{ reservation.paymentMethod === 'onarrival' ? 'Paiement sur place' : 'En ligne' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script setup>
import HeaderComponent from '@/components/HeaderComponentAdmin.vue'
import SideBarComponent from '@/components/SideBarComponent.vue'
import ReservationsChart from '@/components/ReservationsChart.vue'
import DoughnutChart from '@/components/DoughnutChart.vue'
import { ref, onMounted } from 'vue'
import apiClient from '@/services/api'
import { toast } from 'vue3-toastify'

const stats = ref({
  totalReservations: 0,
  totalAmount: 0,
  totalPaid: 0,
  totalDue: 0,
  byPaymentMethod: {
    kkiapay: { count: 0, amount: 0 },
    onarrival: { count: 0, amount: 0 }
  },
  byStatus: {},
  byPaymentChoice: {
    none: 0,
    half: 0,
    full: 0
  },
  paymentStatus: {
    completed: 0,
    pending: 0,
    failed: 0,
    refunded: 0
  }
})

const monthlyStats = ref([])
const recentReservations = ref([])

// Formater les prix
const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price || 0)
}

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  })
}

// Texte pour les méthodes de paiement
const getPaymentMethodText = (method) => {
  const methods = {
    kkiapay: 'KKiaPay',
    onarrival: 'Sur place'
  }
  return methods[method] || method
}

// Couleurs pour les méthodes de paiement
const getPaymentMethodColor = (method) => {
  const colors = {
    kkiapay: 'bg-orange-500',
    onarrival: 'bg-[#D4AF37]'
  }
  return colors[method] || 'bg-gray-500'
}

// Statut des réservations
const getStatusText = (status) => {
  const statusMap = {
    pending: 'En attente',
    pending_onarrival: 'Paiement sur place',
    confirmed: 'Confirmée',
    partially_paid: 'Partiellement payée',
    cancelled: 'Annulée',
    payment_failed: 'Échec paiement'
  }
  return statusMap[status] || status
}

const getStatusClasses = (status) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    pending_onarrival: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-green-100 text-green-800',
    partially_paid: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
    payment_failed: 'bg-purple-100 text-purple-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

// Charger les données
const fetchStats = async () => {
  try {
    // Statistiques globales
    const { data: statsData } = await apiClient.get('/reservations/stats')
    stats.value = statsData.stats

    // Statistiques mensuelles
    const { data: monthlyData } = await apiClient.get('/reservations/stats/monthly-status')
    monthlyStats.value = monthlyData.stats

    // Dernières réservations
    const { data: recentData } = await apiClient.get('/reservations?limit=5&sort=-createdAt')
    recentReservations.value = recentData.data
  } catch (error) {
    console.error('Erreur lors du chargement des statistiques:', error)
  }
}

onMounted(fetchStats)
</script>